/* eslint-disable react/display-name */
import {
  forwardRef,
  Ref,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import dynamic from "next/dynamic";
import { APITypes, PlyrProps, PlyrSource } from "plyr-react";
import styles from "./Player.module.scss";
import { useTrackers } from "@/hooks";

const Plyr = dynamic(
  async () => {
    const { default: PlyrComponent } = await import("plyr-react");
    return function PlyrWithRef({
      playerRef,
      ...props
    }: PlyrProps & { playerRef: Ref<APITypes> }) {
      return <PlyrComponent {...props} ref={playerRef} />;
    };
  },
  { ssr: false }
);

type MediaType = PlyrSource["type"];

const options = {
  controls: [
    "play",
    "progress",
    "current-time",
    "duration",
    "volume",
    "mute",
    "fullscreen",
    "settings",
    "play-large",
  ],
};

export type PlayerProps = Omit<PlyrProps, "onTimeUpdate"> & {
  trackerData?: { [key: string]: string | number | undefined };
  onTimeUpdate?: (event: Plyr.PlyrEvent) => void;
};

const Player = forwardRef(
  ({ trackerData, onTimeUpdate, ...props }: PlayerProps, ref: any) => {
    const [player, setPlayer] = useState<APITypes | null>(null);
    useImperativeHandle(ref, () => player, [player]);
    const { track } = useTrackers();

    useEffect(() => {
      const plyr = player?.plyr;
      if (!plyr?.on) return;

      const getTrackerDetails = (event: Plyr.PlyrEvent, eventId: string) => ({
        eventId,
        position: event.detail.plyr.currentTime,
        total_length: event.detail.plyr.duration,
        ...trackerData,
      });

      const onPlayerPlay = (plyr: Plyr.PlyrEvent) => {
        track("Video Playback Started", {
          ...getTrackerDetails(plyr, "video-playback-started"),
        });
      };
      const onPlayerPause = (plyr: Plyr.PlyrEvent) => {
        track("Video Playback Paused", {
          ...getTrackerDetails(plyr, "video-playback-paused"),
        });
      };
      const onPlayerStalled = (plyr: Plyr.PlyrEvent) => {
        track("Video Playback Interrupted", {
          ...getTrackerDetails(plyr, "video-playback-interrupted"),
        });
      };
      const onPlayerSeeked = (plyr: Plyr.PlyrEvent) => {
        track("Video Playback Seek Completed", {
          ...getTrackerDetails(plyr, "video-playback-seek-completed"),
        });
      };
      // const onPlayerSeeking = (plyr: Plyr.PlyrEvent) => {
      //   track("Video Playback Seek Started", {
      //     ...getTrackerDetails(plyr, "video-playback-seek-started"),
      //   });
      // };
      const onPlayerTimeUpdate = (plyr: Plyr.PlyrEvent) => {
        onTimeUpdate?.(plyr);
      };

      plyr.on("play", onPlayerPlay);
      plyr.on("pause", onPlayerPause);
      plyr.on("stalled", onPlayerStalled);
      plyr.on("timeupdate", onPlayerTimeUpdate);
      plyr.on("seeked", onPlayerSeeked);
      // playerRef.current?.plyr?.on("seeking", onPlayerSeeking);

      return () => {
        plyr.off("play", onPlayerPlay);
        plyr.off("pause", onPlayerPause);
        plyr.off("stalled", onPlayerStalled);
        plyr.off("timeupdate", onPlayerTimeUpdate);
        plyr.off("seeked", onPlayerSeeked);
        // playerRef.current?.plyr?.off("seeking", onPlayerSeeking);
      };
    }, [player, track, trackerData, onTimeUpdate]);

    return (
      <div className={styles.player}>
        <Plyr {...props} playerRef={setPlayer} playsInline options={options} />
      </div>
    );
  }
);

export default Player;
export type { APITypes, PlyrProps, MediaType };
